import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import EnvHelper from "../../components/Utils/EnvHelper";
import cookieManager from "../../components/CookieManager";

export default function ConsoleViewer() {
    const [dismissed, setDismissed] = useState(false);
    const [open, setOpen] = useState(false);
    const [count, setCount] = useState(0);
    const logsRef = useRef([]);
    const originalsRef = useRef({});

    const [query, setQuery] = useState("");
    const [useRegex, setUseRegex] = useState(false);
    const [caseSensitive, setCaseSensitive] = useState(false);
    const [typeFilter, setTypeFilter] = useState({
        log: true,
        info: true,
        warn: true,
        error: true,
        debug: true,
    });

    const [cookiesOpen, setCookiesOpen] = useState(false);
    const [cookiesMap, setCookiesMap] = useState({});

    const readAllCookies = () => {
        try {
            if (cookieManager?.getAll) return cookieManager.getAll();
        } catch {
            console.warn("[Console] cookieManager.getAll() failed");
        }
        const out = {};
        try {
            (document.cookie || "")
                .split(";")
                .map((s) => s.trim())
                .filter(Boolean)
                .forEach((kv) => {
                    const idx = kv.indexOf("=");
                    const k = decodeURIComponent(idx >= 0 ? kv.slice(0, idx) : kv);
                    const v = decodeURIComponent(idx >= 0 ? kv.slice(idx + 1) : "");
                    out[k] = v;
                });
        } catch {}
        return out;
    };
    const refreshCookies = () => setCookiesMap(readAllCookies());

    useLayoutEffect(() => {
        const methods = ["log", "warn", "error", "info", "debug"];
        const originals = {};
        const early = window.__earlyConsole;

        if (early?.buffer?.length) {
            const dumped = early.buffer.splice(0, early.buffer.length);
            logsRef.current.push(
                ...dumped.map((l) => ({
                    type: l.type,
                    time: new Date(l.time),
                    message: l.message,
                }))
            );
            setCount((c) => c + 1);
        }
        methods.forEach((m) => {
            originals[m] = (early?.originals && early.originals[m]) || console[m];
        });

        const handler = (method) => (...args) => {
            const entry = {
                type: method,
                time: new Date(),
                message: (() => {
                    try {
                        return args
                            .map((a) => (typeof a === "string" ? a : JSON.stringify(a)))
                            .join(" ");
                    } catch {
                        return args.map(String).join(" ");
                    }
                })(),
            };
            logsRef.current.push(entry);
            setCount((c) => (c + 1) % 1_000_000);
            originals[method](...args);
        };

        methods.forEach((m) => (console[m] = handler(m)));
        originalsRef.current = originals;

        return () => {
            methods.forEach((m) => (console[m] = originals[m]));
        };
    }, []);

    const clear = () => {
        logsRef.current = [];
        setCount((c) => c + 1);
    };

    const copyAll = async () => {
        const text = logsRef.current
            .map((l) => `[${l.time.toLocaleTimeString()}] ${l.type.toUpperCase()}: ${l.message}`)
            .join("\n");
        try {
            await navigator.clipboard.writeText(text);
        } catch {
            const ta = document.createElement("textarea");
            ta.value = text;
            document.body.appendChild(ta);
            ta.select();
            document.execCommand("copy");
            document.body.removeChild(ta);
        }
    };

    const download = () => {
        const blob = new Blob(
            [
                logsRef.current
                    .map((l) => `[${l.time.toISOString()}] ${l.type.toUpperCase()}: ${l.message}`)
                    .join("\n"),
            ],
            { type: "text/plain;charset=utf-8" }
        );
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "console-logs.txt";
        a.click();
        URL.revokeObjectURL(url);
    };

    const matcher = useMemo(() => {
        if (!query) return null;
        try {
            if (useRegex) {
                return new RegExp(query, caseSensitive ? "g" : "gi");
            }
            const esc = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
            return new RegExp(esc, caseSensitive ? "g" : "gi");
        } catch {
            return null;
        }
    }, [query, useRegex, caseSensitive]);

    const filtered = useMemo(() => {
        const enabledTypes = new Set(
            Object.entries(typeFilter)
                .filter(([, v]) => v)
                .map(([k]) => k)
        );
        const list = logsRef.current;
        if (!matcher) return list.filter((l) => enabledTypes.has(l.type));
        return list.filter((l) => enabledTypes.has(l.type) && matcher.test(l.message));
    }, [count, matcher, typeFilter]);

    const highlight = (text) => {
        if (!matcher || !query) return text;
        const parts = [];
        let lastIndex = 0;
        const rx = new RegExp(matcher.source, matcher.flags.includes("g") ? matcher.flags : matcher.flags + "g");
        let m;
        while ((m = rx.exec(text)) !== null) {
            const start = m.index;
            const end = start + m[0].length;
            if (start > lastIndex) parts.push(text.slice(lastIndex, start));
            parts.push(
                <mark key={start} style={{ background: "#ffd166", color: "#111", padding: "0 2px", borderRadius: 2 }}>
                    {text.slice(start, end)}
                </mark>
            );
            lastIndex = end;
            if (m[0].length === 0) rx.lastIndex++;
        }
        parts.push(text.slice(lastIndex));
        return parts;
    };

    // Disable console in prod
    if (EnvHelper.isProd()) return null;
    if (dismissed) return null;

    return createPortal(
        <>
            <div
                style={{
                    position: "fixed",
                    right: "max(12px, env(safe-area-inset-right))",
                    top: "max(12px, env(safe-area-inset-top))",
                    zIndex: 2147483647,
                    display: "flex",
                    alignItems: "stretch",
                    borderRadius: 999,
                    overflow: "hidden",
                    boxShadow: "0 4px 12px rgba(0,0,0,.25)",
                    fontFamily: "system-ui, sans-serif",
                }}
            >
                <button
                    onClick={() => setOpen((s) => !s)}
                    style={{
                        padding: "10px 14px",
                        border: "none",
                        background: "#111",
                        color: "#fff",
                        cursor: "pointer",
                    }}
                    aria-label="Toggle console overlay"
                >
                    {open ? "Ocultar logs" : `Logs (${logsRef.current.length})`}
                </button>
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        setDismissed(true);
                    }}
                    style={{
                        padding: "10px 12px",
                        border: "none",
                        borderLeft: "1px solid rgba(255,255,255,0.15)",
                        background: "#e53935",
                        color: "#fff",
                        fontSize: 16,
                        lineHeight: 1,
                        cursor: "pointer",
                    }}
                    aria-label="Close console overlay"
                    title="Cerrar"
                >
                    ×
                </button>
            </div>

            {open && (
                <div
                    style={{
                        position: "fixed",
                        right: "max(8px, env(safe-area-inset-right))",
                        top: "calc(max(12px, env(safe-area-inset-top)) + 44px)",
                        width: "min(520px, 96vw)",
                        height: "min(85vh, 90rem)",
                        zIndex: 2147483646,
                        background: "rgba(0,0,0,0.92)",
                        color: "#eaeaea",
                        borderRadius: 10,
                        backdropFilter: "blur(4px)",
                        display: "flex",
                        flexDirection: "column",
                        border: "1px solid rgba(255,255,255,0.1)",
                    }}
                    role="region"
                    aria-label="Console overlay"
                >
                    <div style={{ padding: 8, borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                        <input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search..."
                            style={{
                                width: "100%",
                                padding: "6px 8px",
                                borderRadius: 6,
                                border: "1px solid rgba(255,255,255,0.2)",
                                background: "rgba(255,255,255,0.06)",
                                color: "#fff",
                                fontFamily: "monospace",
                            }}
                        />
                        {/*<label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 12 }}>
                            <input type="checkbox" checked={useRegex} onChange={(e) => setUseRegex(e.target.checked)} />
                            regex
                        </label>*/}
                        <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 12 }}>
                            <input type="checkbox" checked={caseSensitive} onChange={(e) => setCaseSensitive(e.target.checked)} />
                            Aa
                        </label>
                    </div>

                    <div
                        style={{
                            padding: "8px",
                            display: "flex",
                            gap: 10,
                            flexWrap: "wrap",
                            alignItems: "center",
                            borderBottom: "1px solid rgba(255,255,255,0.08)",
                        }}
                    >
                        <TypeToggle type="log" state={typeFilter} setState={setTypeFilter} />
                        <TypeToggle type="warn" state={typeFilter} setState={setTypeFilter} />
                        <TypeToggle type="error" state={typeFilter} setState={setTypeFilter} />
                    </div>

                    <div
                        style={{
                            padding: "8px",
                            display: "flex",
                            gap: 8,
                            alignItems: "center",
                            flexWrap: "wrap",
                            borderBottom: "1px solid rgba(255,255,255,0.08)",
                        }}
                    >
                        <button onClick={clear}>Clear</button>
                        <button onClick={copyAll}>Copy</button>
                        <button onClick={download}>Download</button>
                        <button
                            onClick={() => {
                                const next = !cookiesOpen;
                                setCookiesOpen(next);
                                if (next) refreshCookies();
                            }}
                            title="Cookies"
                        >
                            {cookiesOpen ? "Cerrar Cookies" : "Cookies"}
                        </button>
                        <button style={{ marginLeft: "auto" }} onClick={() => setOpen(false)}>
                            Close
                        </button>
                    </div>

                    {/* Panel de logs */}
                    <div
                        style={{
                            flex: cookiesOpen ? "0 0 55%" : "1 1 auto",
                            overflow: "auto",
                            padding: "6px 10px 10px",
                            fontFamily: "monospace",
                            fontSize: 12,
                            lineHeight: 1.4,
                            whiteSpace: "pre-wrap",
                            borderBottom: cookiesOpen ? "1px solid rgba(255,255,255,0.08)" : "none",
                        }}
                    >
                        {filtered.length === 0 ? (
                            <div style={{ opacity: 0.7 }}>
                                {logsRef.current.length === 0 ? "(sin logs aún)" : "Sin coincidencias"}
                            </div>
                        ) : (
                            filtered.map((l, i) => (
                                <div key={i} style={{ color: colorFor(l.type), marginTop: i === 0 ? 0 : 8 }}>
                                    <span style={{ opacity: 0.7 }}>[{l.time.toLocaleTimeString()}]</span>{" "}
                                    {l.type !== "log" ? l.type.toUpperCase() : ""}: {highlight(l.message)}
                                </div>
                            ))
                        )}
                    </div>

                    {/* Panel de cookies */}
                    {cookiesOpen && (
                        <div
                            style={{
                                flex: "1 1 45%",
                                overflow: "auto",
                                padding: "8px 10px 10px",
                                fontFamily: "monospace",
                                fontSize: 12,
                                lineHeight: 1.4,
                                background: "rgba(255,255,255,0.02)",
                            }}
                        >
                            {Object.keys(cookiesMap).length === 0 ? (
                                <div style={{ opacity: 0.7 }}>(no hay cookies)</div>
                            ) : (
                                Object.entries(cookiesMap)
                                    .sort(([a], [b]) => a.localeCompare(b))
                                    .map(([k, v]) => (
                                        <div key={k} style={{ marginBottom: 8 }}>
                                            <span style={{ color: "#9BEF9B" }}>{k}</span>
                                            <span style={{ opacity: 0.65 }}> : </span>
                                            <span style={{ color: "#eaeaea", wordBreak: "break-word" }}>{typeof v === "string" ? v : JSON.stringify(v)}</span>
                                        </div>
                                    ))
                            )}

                            <div style={{ marginTop: 10, display: "flex", gap: 8, alignItems: "center" }}>
                                <button onClick={refreshCookies}>Refresh</button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </>,
        document.body
    );
}

function TypeToggle({ type, state, setState }) {
    const on = state[type];
    return (
        <label
            style={{
                display: "flex",
                gap: 6,
                alignItems: "center",
                fontSize: 12,
                color: on ? "#fff" : "rgba(255,255,255,0.6)",
            }}
        >
            <input
                type="checkbox"
                checked={on}
                onChange={(e) => setState((s) => ({ ...s, [type]: e.target.checked }))}
            />
            {type}
        </label>
    );
}

function colorFor(type) {
    switch (type) {
        case "error":
            return "#ff6b6b";
        case "warn":
            return "#ffd166";
        default:
            return "#9BEF9B";
    }
}
